import { camelizeKeys, decamelizeKeys } from 'humps';

import { setAuthTokens } from '../apiClient';
import { authClient } from '../authClient';

// PURL ("Personalized URL") pre-authenticated enrollment. A member arrives via a
// personalized link carrying a token + org; the token already proves eligibility,
// so the flow skips company-search / eligibility entirely. Mirrors Sparta's
// onboarding/api/purl.ts, with one difference for the token-based RN client:
// sign-up returns JWT tokens on success (`{ success, token, refresh,
// expires, next_app }`) which we store on apiClient — Sparta returned a
// redirectUrl and relied on a web login cookie, which the RN client has no
// equivalent for.
//
// The flow is two screens: a dedicated DOB-verification entry screen (verify-dob,
// with the 3-attempt/lockout UX) → account creation (sign-up, using the
// already-verified DOB).
//
// authClient camelizes responses (so `paying_org_name` / `attempts_remaining`
// arrive camelCase) and is unauthenticated — every PURL call runs before a
// session exists.
export const PATHS = {
  PURL_VALIDATE: '/api/v1/purl/validate/',
  PURL_VERIFY_DOB: '/api/v1/purl/verify-dob/',
  PURL_SIGN_UP: '/v1/auth/purl-signup',
} as const;

export interface PurlValidateValidResponse {
  status: 'valid';
  eligibilityId: string;
  // Name of the paying organization — used to render the account-creation title.
  payingOrgName?: string;
}

export interface PurlValidateInvalidResponse {
  status: 'invalid';
  redirectUrl: string;
}

export interface PurlValidateAlreadyEnrolledResponse {
  status: 'already_enrolled';
  redirectUrl: string;
}

export type PurlValidateResponse =
  | PurlValidateValidResponse
  | PurlValidateInvalidResponse
  | PurlValidateAlreadyEnrolledResponse;

export type PurlVerifyDobPayload = {
  token: string;
  dobYear: string;
  dobMonth: string;
  dobDay: string;
};

export interface PurlVerifyDobVerifiedResponse {
  status: 'verified';
}

export interface PurlVerifyDobMismatchResponse {
  status: 'mismatch';
  attemptsRemaining: number;
}

export interface PurlVerifyDobLockedResponse {
  status: 'locked';
}

// invalid token / already-enrolled / backend misconfiguration — all terminal for
// the DOB step (Sparta redirects out of the PURL flow on each).
export interface PurlVerifyDobTerminalResponse {
  status: 'invalid' | 'already_enrolled' | 'configuration_error';
}

export type PurlVerifyDobResponse =
  | PurlVerifyDobVerifiedResponse
  | PurlVerifyDobMismatchResponse
  | PurlVerifyDobLockedResponse
  | PurlVerifyDobTerminalResponse;

export type PurlSignUpPayload = {
  token: string;
  dobYear: string;
  dobMonth: string;
  dobDay: string;
  email: string;
  password: string;
  /** Optional — collected on the PURL account-creation screen. */
  phone?: string;
  termsAndConditionsAccepted: boolean;
  informedConsentAccepted: boolean;
  smsAllowed: boolean;
  preferredLanguage: string;
  signedDocuments?: { uuid: string }[];
};

// Raw success body from the backend (camelized): `{ success, token, refresh,
// expires, next_app }` — note there is NO `status` field on success. Kept
// internal; purlSignUp stores the tokens and normalizes to the
// { status: 'created' } result below.
type PurlSignUpSuccessBody = {
  success: boolean;
  token: string;
  refresh: string;
  expires: string;
  nextApp?: string;
};

// Normalized success result returned to callers once the tokens are stored.
export interface PurlSignUpCreatedResponse {
  status: 'created';
}

export interface PurlSignUpAlreadyEnrolledResponse {
  status: 'already_enrolled';
}

export interface PurlSignUpConfigurationErrorResponse {
  status: 'configuration_error';
}

export interface PurlSignUpMismatchResponse {
  status: 'mismatch';
  attemptsRemaining: number;
}

export interface PurlSignUpLockedResponse {
  status: 'locked';
}

export type PurlSignUpResponse =
  | PurlSignUpCreatedResponse
  | PurlSignUpAlreadyEnrolledResponse
  | PurlSignUpConfigurationErrorResponse
  | PurlSignUpMismatchResponse
  | PurlSignUpLockedResponse;

export async function validatePurlToken(token: string, org: string): Promise<PurlValidateResponse> {
  const res = await authClient.get<PurlValidateResponse>(PATHS.PURL_VALIDATE, {
    params: { token, org },
  });
  return res.data;
}

export async function verifyPurlDob(payload: PurlVerifyDobPayload): Promise<PurlVerifyDobResponse> {
  const body = decamelizeKeys(payload);
  // verify-dob returns 400/429 with a { status } body for mismatch / locked /
  // invalid — axios throws on non-2xx, so read the status from the error response
  // here. The camelize interceptor is fulfilled-only, so error bodies arrive
  // snake_case: camelize here or `attempts_remaining` never reaches
  // `attemptsRemaining`. Genuine network/5xx errors (no status field) re-throw.
  try {
    const res = await authClient.post<PurlVerifyDobResponse>(PATHS.PURL_VERIFY_DOB, body);
    return res.data;
  } catch (err: unknown) {
    const data = (err as { response?: { data?: { status?: string } } }).response?.data;
    if (data?.status) {
      return camelizeKeys(data) as PurlVerifyDobResponse;
    }
    throw err;
  }
}

export async function purlSignUp(payload: PurlSignUpPayload): Promise<PurlSignUpResponse> {
  const { signedDocuments, ...rest } = payload;
  const body = decamelizeKeys({
    ...rest,
    // Only include signed_documents when there are some — matches Sparta, which
    // omits the key entirely on an empty list.
    ...(signedDocuments && signedDocuments.length > 0 ? { signedDocuments } : {}),
  });

  // Success is HTTP 200 `{ success: true, token, refresh, expires, next_app }`;
  // failures are 400/429 with a `{ status }` body (mismatch / locked /
  // already_enrolled / configuration_error) — axios throws on those, so read the
  // status from the error response. Genuine network/5xx errors (no status field)
  // re-throw so callers show a generic banner.
  let body200: PurlSignUpSuccessBody;
  try {
    const res = await authClient.post<PurlSignUpSuccessBody>(PATHS.PURL_SIGN_UP, body);
    body200 = res.data;
  } catch (err: unknown) {
    // Error bodies skip the fulfilled-only camelize interceptor — camelize here so
    // e.g. `attempts_remaining` reaches `attemptsRemaining`.
    const errData = (err as { response?: { data?: { status?: string } } }).response?.data;
    if (errData?.status) {
      return camelizeKeys(errData) as PurlSignUpResponse;
    }
    throw err;
  }

  // On success the body carries the JWT tokens — stash them on apiClient so the
  // immediately-following refreshUser() (and every later B2B call) is authed.
  if (body200?.success) {
    await setAuthTokens({
      token: body200.token,
      refresh: body200.refresh,
      expires: body200.expires,
    });
    return { status: 'created' };
  }
  // A 200 that lacks both `success` and a `status` field is a malformed/unexpected
  // body — not a `configuration_error` the backend actually reported. Throw so the
  // caller's catch surfaces a generic error, rather than mislabelling it.
  throw new Error('purl-signup: unexpected 200 response without `success`');
}
